import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Building2, MapPin, Clock, Award } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import RelatedCaseStudies from '@/components/RelatedCaseStudies';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const CommercialEPCDevon = () => {
  const faqs = [
    {
      question: "How much does a Commercial EPC (NDEPC) cost in Devon?",
      answer: "Level 3 Commercial EPC pricing in Devon varies by property type and size. Small offices, shops and retail units (up to 500 m²) typically cost £180–£400. Medium warehouses or industrial units (500–2,000 m²) range from £400–£800. Larger commercial premises may cost £800–£1,500+. Exeter, Plymouth, Torbay and Newton Abbot have minimal or no travel surcharge — we publish any travel fee transparently at the point of booking."
    },
    {
      question: "Do you cover all of Devon for NDEPC and NDEA services?",
      answer: "Yes. As a Devon-based NDEA (Non-Domestic Energy Assessor) we cover the entire county including Exeter, Plymouth, Torbay (Torquay, Paignton, Brixham), Barnstaple, Bideford, Ilfracombe, Exmouth, Sidmouth, Honiton, Tiverton, Okehampton, Tavistock, Totnes, Dartmouth, Kingsbridge, Salcombe and all South Hams, Mid Devon, North Devon, East Devon and West Devon districts."
    },
    {
      question: "What is a Level 3 Commercial EPC and will my Devon property qualify?",
      answer: "A Level 3 NDEPC is the most common non-domestic assessment and covers offices, shops, warehouses, light industrial units, pubs, cafés, B&Bs, surgeries, nurseries and similar buildings. The vast majority of Devon commercial properties we survey fall into Level 3, which keeps the modelling proportionate and the fee competitive while still producing a fully lodged, legally compliant certificate."
    },
    {
      question: "Do you also do SBEM calculations and new-build EPCs in Devon?",
      answer: "Yes. Alongside the core Commercial EPC we provide full SBEM calculations for new-build and major refurbishment projects that need Part L Building Regulations compliance, plus the as-built commercial EPC required for Building Control sign-off. See our main Commercial EPC pillar page for more detail."
    },
    {
      question: "How long does a Commercial EPC assessment take in Devon?",
      answer: "The on-site survey typically takes 1–3 hours depending on property size and complexity. For Devon properties we aim to attend site within 5–7 working days of booking, with the lodged NDEPC emailed within a further 5–7 working days. Expedited same-week service is available for urgent Devon commercial transactions."
    },
    {
      question: "My Devon commercial building is rated F or G — what now?",
      answer: "Since April 2023 it is unlawful to continue to let a commercial property rated F or G unless a valid MEES exemption is registered. We pair the EPC with a separate Commercial Energy Advice (MEES consultancy) report that models realistic upgrades — LED re-lamping, controls, insulation, heat pump retrofit, solar PV — with indicative cost and payback, so Devon landlords have a defensible improvement plan."
    }
  ];

  const devonAreas = [
    "Exeter - City centre, Science Park, Sowton Industrial Estate, Marsh Barton",
    "Plymouth - Derriford, Millbay, Sutton Harbour, Plymstock, Langage",
    "Torbay - Torquay, Paignton, Brixham",
    "Barnstaple - North Devon business parks and retail parks",
    "Newton Abbot - Heathfield, Brunel and town centre",
    "Exmouth, Sidmouth, Honiton - East Devon towns",
    "Tiverton, Crediton, Cullompton - Mid Devon",
    "Tavistock, Okehampton - West Devon",
    "Totnes, Dartmouth, Kingsbridge, Salcombe - South Hams",
    "Ilfracombe, Bideford - North Devon coast"
  ];

  return (
    <>
      <Helmet>
        <title>Commercial EPC Devon | NDEPC, NDEA, Level 3 | Exeter, Plymouth, Torbay | Energy Performance Direct</title>
        <meta name="description" content="Accredited NDEA Commercial EPC (NDEPC) assessments across Devon from £180. Level 3 EPCs and SBEM calculations. Exeter, Plymouth, Torbay, Barnstaple — MEES advice included. Call 01392 980077." />
        <meta name="keywords" content="Commercial EPC Devon, NDEPC Devon, Non-Domestic Energy Performance Certificate Devon, NDEA Devon, Level 3 EPC Devon, Commercial EPC Exeter, Commercial EPC Plymouth, Commercial EPC Torbay, SBEM Devon, MEES commercial Devon" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/commercial-epc-devon" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Commercial Energy Performance Certificate (NDEPC)",
            "name": "Commercial EPC Devon",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Energy Performance Direct",
              "telephone": "+44-1392-980077",
              "email": "info@energyperformancedirect.co.uk",
              "priceRange": "£180 - £1500"
            },
            "areaServed": { "@type": "AdministrativeArea", "name": "Devon" }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(f => ({
              "@type": "Question",
              "name": f.question,
              "acceptedAnswer": { "@type": "Answer", "text": f.answer }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <section className="py-16 bg-gradient-to-br from-green-50 via-cyan-50 to-green-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">NDEA Accredited · Covering All Devon Districts</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                Commercial EPC (NDEPC) Assessments Across Devon
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                Accredited Non-Domestic Energy Assessors (NDEA) for Devon businesses. Level 3 Commercial EPCs, SBEM calculations and MEES advice — Exeter, Plymouth, Torbay, Barnstaple and every Devon town.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link to="/booking" data-testid="devon-book-btn">
                  <Button className="bg-gradient-to-r from-green-600 to-cyan-500 hover:from-green-700 hover:to-cyan-600 text-white px-8 py-6 text-lg rounded-full shadow-lg">
                    Get a Quote in 2 Minutes
                  </Button>
                </Link>
                <a href="tel:01392980077" data-testid="devon-call-btn">
                  <Button variant="outline" className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Award className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">NDEA Qualified</h3>
                  <p className="text-sm text-gray-600">Elmhurst accredited Non-Domestic Energy Assessors</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Clock className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">5–7 Day Delivery</h3>
                  <p className="text-sm text-gray-600">Fast NDEPC turnaround from survey to lodgement</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <MapPin className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">Countywide Coverage</h3>
                  <p className="text-sm text-gray-600">All Devon districts and industrial estates</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Building2 className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">From £180</h3>
                  <p className="text-sm text-gray-600">Competitive Level 3 pricing</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Commercial EPC Services Across Devon</h2>

              <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
                <p>
                  Energy Performance Direct provides professional Non-Domestic Energy Performance Certificate (NDEPC) assessments for commercial buildings throughout Devon. Based in Exeter and operating across the county since 2009, our NDEA assessors know Devon&rsquo;s commercial property stock intimately &mdash; from modern business parks in Exeter and Plymouth to historic market-town retail units, coastal hotels in Torbay and Ilfracombe, agricultural buildings with commercial use, and industrial estates in Barnstaple and Newton Abbot.
                </p>

                <p>
                  A <Link to="/commercial-epc" className="text-green-600 hover:text-green-700 underline">Commercial EPC (NDEPC)</Link> is legally required whenever you sell, rent or construct a commercial building in Devon. The certificate rates the building A–G on the SBEM asset scale and is valid for 10 years. Most Devon properties fall within the Level 3 category &mdash; offices, shops, small industrial units, pubs, cafés and surgeries &mdash; which is our everyday specialism.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Devon Coverage &mdash; From Plymouth to Barnstaple</h3>

                <p>
                  We provide NDEPC assessments across every Devon district. Our assessors regularly survey Plymouth&rsquo;s city centre and waterfront developments, Exeter&rsquo;s Science Park and Sowton Industrial Estate, Torbay&rsquo;s seafront hotels and entertainment venues, Barnstaple&rsquo;s North Devon business parks, and market towns throughout East Devon, Mid Devon, West Devon, South Hams and North Devon.
                </p>

                <div className="bg-white border border-gray-200 rounded-lg p-6 my-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">Devon Areas We Cover:</h4>
                  <ul className="space-y-2">
                    {devonAreas.map((area, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{area}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">SBEM &amp; new-build EPCs across Devon</h3>
                <p>
                  In addition to standard NDEPCs we produce full SBEM calculations for new-build and refurbishment projects needing Part L Building Regulations compliance, plus the as-built commercial EPC for Building Control sign-off. See our <Link to="/sbem-explained" className="text-green-600 hover:text-green-700 underline">SBEM explained guide</Link>.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">MEES consultancy for Devon landlords</h3>
                <p>
                  Since April 2023 it is unlawful to let a Devon commercial property rated F or G without a registered exemption. Under DESNZ&rsquo;s June 2026 interim response, the 2027 EPC C milestone has been dropped and a proposed EPC B by 2031 will apply only to buildings over 1,000 m² (pending secondary legislation); smaller Devon properties remain at the current Band E minimum. Our <Link to="/services/commercial_advice" className="text-green-600 hover:text-green-700 underline">Commercial Energy Advice (MEES) consultancy</Link> sits alongside the EPC &mdash; we model realistic upgrades (LED re-lamping, heating controls, roof and cavity insulation, heat pump retrofit, solar PV) with cost and payback so Devon landlords have a defensible improvement plan for tenants, lenders and insurers. See our <Link to="/case-study-london-mees-unit-split" className="text-green-600 hover:text-green-700 underline">MEES case study</Link> for a worked example.
                </p>

                <p>
                  Commercial EPC pricing in Devon depends on property type, size and location. Indicative pricing: small offices/retail (up to 500 m²) £180–£400, medium warehouses (500–2,000 m²) £400–£800, large properties £800–£1,500+. Exeter, Plymouth, Torbay and Newton Abbot have minimal travel surcharge; more remote Devon locations are priced transparently based on distance.
                </p>
              </div>

              <div className="mt-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">Devon Commercial EPC FAQs</h2>
                <div className="space-y-6">
                  {faqs.map((faq, idx) => (
                    <Card key={idx} className="border-none shadow-md">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                        <p className="text-gray-700">{faq.answer}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="mt-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Industrial &amp; Warehouse EPCs in Devon</h2>
                <p className="text-gray-700 mb-6">
                  Looking specifically for an industrial or warehouse EPC in Devon? Our specialist industrial assessors cover Marsh Barton, Sowton, Langage and every major Devon trading estate.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Link to="/industrial-epc-devon" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <h3 className="font-semibold mb-1">Industrial EPCs Devon</h3>
                    <p className="text-sm text-gray-600">Marsh Barton, Sowton, Langage and more</p>
                  </Link>
                  <Link to="/warehouse-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <h3 className="font-semibold mb-1">Warehouse EPCs</h3>
                    <p className="text-sm text-gray-600">Distribution centres and storage facilities</p>
                  </Link>
                  <Link to="/commercial-epc" className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <h3 className="font-semibold mb-1">All Commercial EPC Services</h3>
                    <p className="text-sm text-gray-600">NDEPC, SBEM &amp; MEES advice</p>
                  </Link>
                </div>
              </div>

              <div className="mt-16 bg-gradient-to-r from-green-600 to-cyan-500 rounded-2xl p-8 text-white text-center">
                <h2 className="text-3xl font-bold mb-4">Book Your Devon Commercial EPC</h2>
                <p className="text-lg mb-6">
                  Professional NDEPCs across all Devon districts. Call 01392 980077 or book online.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/booking">
                    <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">
                      Book Online Now
                    </Button>
                  </Link>
                  <a href="tel:01392980077">
                    <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-6 text-lg rounded-full">
                      <Phone className="w-5 h-5 mr-2" />
                      Call 01392 980077
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <RelatedCaseStudies filterType="commercial" title="Devon commercial buildings we've assessed" subtitle="See how the same NDEA service has worked for other commercial buildings in the South West." limit={3} />

        <Footer />
      </div>
    </>
  );
};

export default CommercialEPCDevon;
